// NeueRechnungDialog.tsx — Schritt 1 der Rechnungsanlage: den Kunden wählen.
//
// Bewusst schlank: die Rechnung entsteht als ENTWURF, sobald der Kunde steht.
// Die Positionen kommen danach im Detail dazu — dort ist der Vertrag auszuwählen
// und der Beitrag einzutragen, und dort sieht man sofort, was die Rechnung wird.
// Ein Dialog, der Kopf UND Positionen abfragt, bevor irgendetwas existiert,
// müsste den halben Detailbildschirm nachbauen und ginge bei Abbruch verloren.
//
// Die Nummer wird beim Anlegen sofort vergeben — die Rechnung ist damit
// referenzierbar. Gebucht (und damit fällig) wird sie erst später.

import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { apiFetch } from "../../utils/apiFetch";
import { useDebouncedValue } from "../../utils/useDebouncedValue";
import { serverMeldung } from "./serverMeldung";
import "./contactRedesign.css";
import { alleDokumente, aktivesDokument } from "../windowManager/browserFenster/fensterRegister";
import { istDom } from "../windowManager/browserFenster/domArt";

type KontaktTreffer = {
  id: number;
  lastname?: string | null;
  firstName?: string | null;
};

type Props = {
  onClose: () => void;
  onAngelegt: (invoiceId: number) => void;
  /** Kunde bereits bekannt (Aufruf aus dem Kontakt heraus) — Suche entfällt. */
  vorgabeContactId?: number;
  vorgabeName?: string;
};

const anzeigeName = (k: KontaktTreffer): string =>
  [k.lastname, k.firstName].filter(Boolean).join(", ") || `Kontakt ${k.id}`;

const NeueRechnungDialog: React.FC<Props> = ({
  onClose,
  onAngelegt,
  vorgabeContactId,
  vorgabeName,
}) => {
  const [suche, setSuche] = useState("");
  const debounced = useDebouncedValue(suche, 300);
  const [treffer, setTreffer] = useState<KontaktTreffer[]>([]);
  const [suchend, setSuchend] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<KontaktTreffer | null>(
    vorgabeContactId ? { id: vorgabeContactId, lastname: vorgabeName ?? null } : null,
  );

  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  // Der Dialog behauptet per `aria-modal`, alles dahinter sei unerreichbar —
  // dann muss er sich auch so verhalten: Escape schliesst, und der Fokus kehrt
  // beim Schliessen zum auslösenden Knopf zurück, statt am Seitenanfang zu landen.
  const vorherFokussiert = useRef<Element | null>(null);
  useEffect(() => {
    vorherFokussiert.current = aktivesDokument().activeElement;
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    alleDokumente.addEventListener("keydown", beiTaste);
    return () => {
      alleDokumente.removeEventListener("keydown", beiTaste);
      const zurueck = vorherFokussiert.current;
      if (istDom(zurueck, "HTMLElement")) zurueck.focus();
    };
  }, [onClose]);

  useEffect(() => {
    if (vorgabeContactId) return undefined;
    const text = debounced.trim();
    if (text.length < 2) {
      setTreffer([]);
      return undefined;
    }
    const ctrl = new AbortController();
    setSuchend(true);
    apiFetch<KontaktTreffer[]>(`contact?search=${encodeURIComponent(text)}&limit=20`, {
      signal: ctrl.signal,
    })
      .then(({ data }) => setTreffer(data ?? []))
      .catch(() => {
        if (!ctrl.signal.aborted) setTreffer([]);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setSuchend(false);
      });
    return () => ctrl.abort();
  }, [debounced, vorgabeContactId]);

  const anlegen = useCallback(async () => {
    if (!gewaehlt || speichert) return;
    setSpeichert(true);
    setFehler(null);
    try {
      const res = await apiFetch<{ id: number }>("invoices", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contactId: gewaehlt.id }),
      });
      if (res.data?.id) onAngelegt(res.data.id);
      else setFehler("Die Rechnung wurde angelegt, konnte aber nicht geöffnet werden.");
    } catch (e) {
      setFehler(serverMeldung(e, "Die Rechnung konnte nicht angelegt werden."));
    } finally {
      setSpeichert(false);
    }
  }, [gewaehlt, speichert, onAngelegt]);

  return (
    <div
      className="kvp-rd-scrim"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dm" style={{ width: "min(620px, 100%)" }} role="dialog" aria-modal="true">
        <div className="dm-head">
          <div className="dm-head-top">
            <div className="dm-titles">
              <div className="dm-kicker">Zahlungsverkehr</div>
              <h2>Neue Rechnung</h2>
            </div>
            <button className="dm-x" onClick={onClose} aria-label="Schliessen">
              ×
            </button>
          </div>
        </div>

        <div className="dm-body">
          <div className="kvp-rd inv-kundensuche">
            {!vorgabeContactId && (
              <>
                <div className="inv-feld">
                  <label htmlFor="rechnung-kunde">Kunde</label>
                  <input
                    id="rechnung-kunde"
                    className="inp"
                    autoFocus
                    placeholder="Name oder Firma suchen …"
                    value={suche}
                    onChange={(e) => {
                      setSuche(e.target.value);
                      setGewaehlt(null);
                    }}
                  />
                </div>

                {suche.trim().length >= 2 && (
                  <div className="inv-treffer">
                    {suchend && <div className="leer">Wird gesucht …</div>}
                    {!suchend && treffer.length === 0 && (
                      <div className="leer">Kein Kontakt gefunden.</div>
                    )}
                    {treffer.map((k) => (
                      <button
                        key={k.id}
                        type="button"
                        className={gewaehlt?.id === k.id ? "gewaehlt" : undefined}
                        onClick={() => setGewaehlt(k)}
                      >
                        {anzeigeName(k)}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {gewaehlt && (
              <>
                <div className="inv-feld">
                  <label>Gewählter Kunde</label>
                  <div className="inv-wert">{anzeigeName(gewaehlt)}</div>
                </div>

              </>
            )}

            {fehler && <div className="kvp-rd rd-error">{fehler}</div>}
          </div>
        </div>

        <div className="dm-foot">
          <button className="inv-btn" onClick={onClose} disabled={speichert}>
            Abbrechen
          </button>
          <button className="cd-save" onClick={anlegen} disabled={!gewaehlt || speichert}>
            {speichert ? "Wird angelegt …" : "Entwurf anlegen"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NeueRechnungDialog;
